export const NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

// All the possible chord modifiers we support
const MODIFIERS = [
  'm', 'dim', 'aug', 'sus2', 'sus4', 'maj7', 'm7', '7', 
  'maj9', 'm9', '9', 'maj11', 'm11', '11',
  'add9', 'madd9', '6', 'm6', '5',
  'maj', 'sus', 'add'
];

// Key signatures: major scales with correct accidentals
export const KEY_SIGNATURES = {
  'C':  ['C', 'D', 'E', 'F', 'G', 'A', 'B'],
  'G':  ['G', 'A', 'B', 'C', 'D', 'E', 'F#'],
  'D':  ['D', 'E', 'F#', 'G', 'A', 'B', 'C#'],
  'A':  ['A', 'B', 'C#', 'D', 'E', 'F#', 'G#'],
  'E':  ['E', 'F#', 'G#', 'A', 'B', 'C#', 'D#'],
  'B':  ['B', 'C#', 'D#', 'E', 'F#', 'G#', 'A#'],
  'F#': ['F#', 'G#', 'A#', 'B', 'C#', 'D#', 'E#'],
  'C#': ['C#', 'D#', 'E#', 'F#', 'G#', 'A#', 'B#'],
  'F':  ['F', 'G', 'A', 'Bb', 'C', 'D', 'E'],
  'Bb': ['Bb', 'C', 'D', 'Eb', 'F', 'G', 'A'],
  'Eb': ['Eb', 'F', 'G', 'Ab', 'Bb', 'C', 'D'],
  'Ab': ['Ab', 'Bb', 'C', 'Db', 'Eb', 'F', 'G'],
  'Db': ['Db', 'Eb', 'F', 'Gb', 'Ab', 'Bb', 'C'],
  'Gb': ['Gb', 'Ab', 'Bb', 'Cb', 'Db', 'Eb', 'F'],
  'Cb': ['Cb', 'Db', 'Eb', 'Fb', 'Gb', 'Ab', 'Bb'],
};

// Expand KEY_SIGNATURES to include natural minor keys
export const MINOR_KEY_SIGNATURES = {
  'Am': ['A', 'B', 'C', 'D', 'E', 'F', 'G'],
  'Em': ['E', 'F#', 'G', 'A', 'B', 'C', 'D'],
  'Bm': ['B', 'C#', 'D', 'E', 'F#', 'G', 'A'],
  'F#m': ['F#', 'G#', 'A', 'B', 'C#', 'D', 'E'],
  'C#m': ['C#', 'D#', 'E', 'F#', 'G#', 'A', 'B'],
  'G#m': ['G#', 'A#', 'B', 'C#', 'D#', 'E', 'F#'],
  'D#m': ['D#', 'E#', 'F#', 'G#', 'A#', 'B', 'C#'],
  'A#m': ['A#', 'B#', 'C#', 'D#', 'E#', 'F#', 'G#'],
  'Dm': ['D', 'E', 'F', 'G', 'A', 'Bb', 'C'],
  'Gm': ['G', 'A', 'Bb', 'C', 'D', 'Eb', 'F'],
  'Cm': ['C', 'D', 'Eb', 'F', 'G', 'Ab', 'Bb'],
  'Fm': ['F', 'G', 'Ab', 'Bb', 'C', 'Db', 'Eb'],
  'Bbm': ['Bb', 'C', 'Db', 'Eb', 'F', 'Gb', 'Ab'],
  'Ebm': ['Eb', 'F', 'Gb', 'Ab', 'Bb', 'Cb', 'Db'],
  'Abm': ['Ab', 'Bb', 'Cb', 'Db', 'Eb', 'Fb', 'Gb'],
};

// Merge major and minor keys for selection
export const ALL_KEYS = { ...KEY_SIGNATURES, ...MINOR_KEY_SIGNATURES };

// Helper: is this a minor key?
export function isMinorKey(key) {
  return key.endsWith('m');
}

// Helper: get scale for a key (major or minor)
export function getScale(key) {
  return isMinorKey(key) ? MINOR_KEY_SIGNATURES[key] : KEY_SIGNATURES[key];
}

// Roman numerals for major and minor scale degrees
export const ROMAN_NUMERALS_MAJOR = ['I', 'ii', 'iii', 'IV', 'V', 'vi', 'vii°'];
export const ROMAN_NUMERALS_MINOR = ['i', 'ii°', 'III', 'iv', 'v', 'VI', 'VII'];

// Get Roman numeral for a chord in a key
export function getRomanNumeral(chord, key) {
  const scale = getScale(key);
  if (!scale) return '';
  const deg = getScaleDegree(chord, key);
  if (!deg) return '';
  let numerals = isMinorKey(key) ? ROMAN_NUMERALS_MINOR : ROMAN_NUMERALS_MAJOR;
  let numeral = numerals[deg.degree];
  // Add 7, 9, etc. if present in modifier
  if (deg.modifier && /7|9|11|13/.test(deg.modifier)) {
    numeral += deg.modifier.match(/7|9|11|13/)[0];
  }
  return numeral;
}

// Helper: does this key use sharps?
export function usesSharps(key) {
  const sharpKeys = ['C', 'G', 'D', 'A', 'E', 'B', 'F#', 'C#'];
  return sharpKeys.includes(key);
}

// Helper: get scale degree (0-based) and modifier from chord in a key
export function getScaleDegree(chord, key) {
  if (!chord) return null;
  const match = chord.match(/^([A-G])(#|b)?(.*)$/);
  if (!match) return null;
  const [, root, accidental = '', modifier = ''] = match;
  const scale = getScale(key);
  if (!scale) return null;
  let rootNote = root + accidental;
  let degree = scale.findIndex(n => n === rootNote);
  if (degree === -1) {
    const enharmonics = {
      'Db': 'C#', 'Eb': 'D#', 'Gb': 'F#', 'Ab': 'G#', 'Bb': 'A#',
      'Cb': 'B', 'Fb': 'E', 'E#': 'F', 'B#': 'C',
      'C#': 'Db', 'D#': 'Eb', 'F#': 'Gb', 'G#': 'Ab', 'A#': 'Bb',
    };
    const alt = enharmonics[rootNote];
    if (alt) degree = scale.findIndex(n => n === alt);
  }
  if (degree === -1) return null;
  return { degree, modifier };
}

// Helper: get chord from scale degree and modifier in a key
export function getChordFromDegree(degree, modifier, key) {
  const scale = getScale(key);
  if (!scale) return null;
  let note = scale[degree];
  return note + modifier;
}

// Music-theory-accurate chord transposition
export const transposeChord = (chord, fromKey, toKey) => {
  if (!chord) return chord;
  if (chord.includes('/')) {
    const [upperChord, bassNote] = chord.split('/');
    const transposedUpper = transposeChord(upperChord, fromKey, toKey);
    const transposedBass = transposeChord(bassNote, fromKey, toKey);
    return `${transposedUpper}/${transposedBass}`;
  }
  const deg = getScaleDegree(chord, fromKey);
  if (!deg) return chord; // fallback: not in scale
  return getChordFromDegree(deg.degree, deg.modifier, toKey);
};

// Validate a single chord
export const isValidChord = (chord) => {
  if (!chord || chord.trim() === '') return true;
  
  // Basic chord pattern
  const rootNote = '[A-G]';
  const accidental = '[#b]?';
  const modifier = '(m|maj|dim|aug|sus|add)?[0-9]*';
  const slashChord = `(/${rootNote}${accidental})?`;
  
  const chordPattern = new RegExp(`^${rootNote}${accidental}${modifier}${slashChord}$`);
  return chordPattern.test(chord.trim());
};

// Enhanced validation that returns detailed error info
export const validateChordInput = (input, sectionType, lineIndex) => {
  if (!input) return { isValid: true };
  
  const chords = input.trim().split(/\s+/);
  let invalidChord = null;
  let invalidIndex = -1;

  const allValid = chords.every((chord, index) => {
    const isValid = isValidChord(chord);
    if (!isValid && !invalidChord) {
      invalidChord = chord;
      invalidIndex = index;
    }
    return isValid;
  });

  return {
    isValid: allValid,
    error: !allValid ? {
      chord: invalidChord,
      position: invalidIndex,
      section: sectionType,
      lineIndex: lineIndex,
      message: `Invalid chord "${invalidChord}" in ${sectionType}, line ${lineIndex + 1}`
    } : null
  };
};

export const getChordPositions = (lyrics, chords) => {
  const chordArray = chords.trim().split(/\s+/);
  const positions = [];
  let currentPosition = 0;

  chordArray.forEach(chord => {
    const nextWordMatch = lyrics.slice(currentPosition).match(/\S+/);
    if (nextWordMatch) {
      positions.push({
        chord,
        position: currentPosition + nextWordMatch.index
      });
      currentPosition += nextWordMatch.index + nextWordMatch[0].length;
    }
  });

  return positions;
};

// Get scale degree as a number (1-7) for a chord in a key
export function getDegreeNumber(chord, key) {
  const deg = getScaleDegree(chord, key);
  if (!deg) return '';
  return (deg.degree + 1).toString();
} 